import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "react-hot-toast";
import Sidebar from "../../components/Sidebar";
import {
  useUserById,
  useUpdateUser,
} from "../../services/queries/queries";

const getIdOptions = (country) => {
  switch (country?.toLowerCase()) {
    case "nigeria":
      return [
        { label: "BVN", value: "NIGERIAN_BVN_VERIFICATION" },
        { label: "NIN", value: "NIGERIAN_NIN" },
      ];
    case "ghana":
      return [
        { label: "International Passport", value: "GHANA_INTERNATIONAL_PASSPORT" },
      ];
    case "kenya":
      return [
        { label: "National ID", value: "KENYAN_NATIONAL_ID" },
        { label: "Alien Card", value: "KENYAN_ALIEN_CARD" },
        { label: "Passport", value: "KENYA_PASSPORT" },
      ];
    case "south africa":
      return [
        { label: "National ID", value: "SOUTHAFRICAN_NATIONAL_ID" },
        { label: "Green Book", value: "SOUTHAFRICAN_GREEN_BOOK" },
        { label: "Passport", value: "SOUTHAFRICAN_PASSPORT" },
        { label: "Resident Card", value: "SOUTHAFRICAN_RESIDENT_CARD" },
        { label: "Driver's License", value: "SOUTHAFRICAN_DRIVERS_LICENSE" },
      ];
    default:
      return [
        { label: "International Passport", value: "INTERNATIONAL_PASSPORT" },
        { label: "National ID", value: "NATIONAL_ID" },
        { label: "Driver's License", value: "DRIVERS_LICENSE" },
        { label: "Residence Permit", value: "RESDIENCE_PERMIT" },
      ];
  }
};

const EditUser = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  
  const [values, setValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    country: "",
    address: "",
    postalCode: "",
    state: "",
    city: "",
    kyc_metadata: {
      id_type: "passport",
      id_number: ""
    },
    isActive: true,
    kyc_status: "",
    isHighRisk: false,
    isOtcCustomer: false,
    otcPlatformPercentage: 0,
    reservePercentage: 0,
  });

  // Use the useUserById query hook
  const { data: response, isLoading, error } = useUserById(uuid);
  const userData = response?.user;
  const kycData = response?.kyc;

  const { mutate, isPending } = useUpdateUser();

  useEffect(() => {
    if (userData) {
      setValues({
        firstName: userData.firstName || "",
        lastName: userData.lastName || "",
        email: userData.email || "",
        phoneNumber: userData.phoneNumber || "",
        country: userData?.country || "",
        address: kycData?.address || "",
        postalCode: kycData?.postalCode || "",
        state:kycData?.state || "",
        city: kycData?.city || "",
        kyc_metadata: {
          // Preserve every existing key (id_image, selfie_image, etc.) - this
          // form only edits id_type/id_number, and the backend does a full
          // column replace with whatever object is submitted here, so
          // dropping unknown keys here deletes previously-uploaded KYC images.
          ...(kycData?.kyc_metadata || {}),
          id_type: kycData?.kyc_metadata?.id_type || getIdOptions(userData?.country)[0]?.value || "",
          id_number: kycData?.kyc_metadata?.id_number || ""
        },
        isActive: userData?.isActive ?? true,
        kyc_status: kycData?.kyc_status,
        isHighRisk: userData?.isHighRisk ?? false,
        isOtcCustomer: userData?.isOtcCustomer ?? false,
        otcPlatformPercentage: userData?.otcPlatformPercentage != null ? Number(userData.otcPlatformPercentage) : 0,
        reservePercentage: userData?.reservePercentage != null ? Number(userData.reservePercentage) : 0,
      });
    }
  }, [userData, kycData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("kyc_metadata.")) {
      const field = name.split(".")[1];
      setValues(prev => ({
        ...prev,
        kyc_metadata: {
          ...prev.kyc_metadata,
          [field]: value
        }
      }));
    } else if (name === "country") {
      const newOptions = getIdOptions(value);
      setValues(prev => {
        const currentIdType = prev.kyc_metadata.id_type;
        const isValid = newOptions.some(opt => opt.value === currentIdType);
        return {
          ...prev,
          country: value,
          kyc_metadata: {
            ...prev.kyc_metadata,
            id_type: isValid ? currentIdType : (newOptions[0]?.value || "")
          }
        };
      });
    } else {
      setValues(prev => ({
        ...prev,
        [name]: ["isActive", "isHighRisk", "isOtcCustomer"].includes(name) ? e.target.checked : value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payloadToSubmit = {
        ...values,
        otcPlatformPercentage: values.otcPlatformPercentage === "" ? 0 : Number(values.otcPlatformPercentage),
        reservePercentage: values.reservePercentage === "" ? 0 : Number(values.reservePercentage),
      };

      mutate({ uuid, payload: payloadToSubmit }, {
        onSuccess: (res) => {
          console.log(res)
          toast.success("User updated successfully");
          navigate(`/users/${uuid}`);
        },
        onError: (err) => {
          toast.error(err?.response?.data?.message || "Failed to update user");
        }
      });
    } catch (err) {
      console.error("Error updating user:", err);
    }
  };

  if (isLoading) {
    return (
      <div className="flex overflow-hidden flex-col h-screen md:flex-row">
        {/* Sidebar handles its own desktop/mobile rendering */}
        <Sidebar />
        
        {/* Main content — scrolls independently */}
        <div className="flex flex-1 justify-center items-center">
          <Loader2 className="w-8 h-8 text-emerald-800 animate-spin" />
          <p className="ml-2 text-gray-600">Loading user data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex overflow-hidden flex-col h-screen md:flex-row">
        {/* Sidebar handles its own desktop/mobile rendering */}
        <Sidebar />
        
        {/* Main content — scrolls independently */}
        <div className="flex flex-1 justify-center items-center">
          <p className="text-red-600">Error: {error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex overflow-hidden flex-col h-screen md:flex-row">
      {/* Sidebar handles its own desktop/mobile rendering */}
      <Sidebar />
      
      {/* Main content — scrolls independently */}
      <div className="overflow-y-auto flex-1 p-4 py-7 mb-9 bg-white md:py-7">
        <div className="px-2 mx-auto md:px-8">
          <div className="flex justify-between items-center mb-8">
            <div className="space-y-1">
              <h1 className="py-2 text-xl font-semibold md:text-2xl">Edit Consumer</h1>
              <button
                className="flex items-center text-[#013941] hover:underline"
                onClick={() => navigate(-1)}
              >
                <ArrowLeft className="w-4 h-4 mr-1 text-[#013941]" />
                Back
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-8 border border-[#CCF6E5] rounded-3xl px-4 py-6 md:px-8 md:py-8 shadow-sm">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Name Fields */}
              <div className="space-y-4">
                <div>
                  <label className="block mb-2 text-sm font-semibold text-gray-700">First name</label>
                  <input
                    type="text"
                    name="firstName"
                    value={values.firstName}
                    onChange={handleChange}
                    className="outline-none font-sans w-full p-3 border border-gray-200 rounded-full text-[#000E10] text-base focus:border-emerald-500 transition-colors"
                    
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-semibold text-gray-700">Last name</label>
                  <input
                    type="text"
                    name="lastName"
                    value={values.lastName}
                    onChange={handleChange}
                    className="outline-none font-sans w-full p-3 border border-gray-200 rounded-full text-[#000E10] text-base focus:border-emerald-500 transition-colors"
                    
                  />
                </div>
              </div>

              {/* Contact Fields */}
              <div className="space-y-4">
                <div>
                  <label className="block mb-2 text-sm font-semibold text-gray-700">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    className="outline-none font-sans w-full p-3 border border-gray-200 rounded-full text-[#000E10] text-base focus:border-emerald-500 transition-colors"
                    
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-semibold text-gray-700">Phone number</label>
                  <input
                    type="text"
                    name="phoneNumber"
                    value={values.phoneNumber}
                    onChange={handleChange}
                    className="outline-none font-sans w-full p-3 border border-gray-200 rounded-full text-[#000E10] text-base focus:border-emerald-500 transition-colors"
                    
                  />
                </div>
              </div>

              {/* Location Fields */}
              <div className="space-y-4">
                <div>
                  <label className="block mb-2 text-sm font-semibold text-gray-700">Country</label>
                  <input
                    type="text"
                    name="country"
                    value={values.country}
                    onChange={handleChange}
                    className="outline-none font-sans w-full p-3 border border-gray-200 rounded-full text-[#000E10] text-base focus:border-emerald-500 transition-colors"
                    
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-semibold text-gray-700">State</label>
                  <input
                    type="text"
                    name="state"
                    value={values.state}
                    onChange={handleChange}
                    className="outline-none font-sans w-full p-3 border border-gray-200 rounded-full text-[#000E10] text-base focus:border-emerald-500 transition-colors"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block mb-2 text-sm font-semibold text-gray-700">City</label>
                  <input
                    type="text"
                    name="city"
                    value={values.city}
                    onChange={handleChange}
                    className="outline-none font-sans w-full p-3 border border-gray-200 rounded-full text-[#000E10] text-base focus:border-emerald-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-semibold text-gray-700">Postal Code</label>
                  <input
                    type="text"
                    name="postalCode"
                    value={values.postalCode}
                    onChange={handleChange}
                    className="outline-none font-sans w-full p-3 border border-gray-200 rounded-full text-[#000E10] text-base focus:border-emerald-500 transition-colors"
                  />
                </div>
              </div>

              {/* Address (Full width or split) */}
              <div className="md:col-span-2">
                <label className="block mb-2 text-sm font-semibold text-gray-700">Address</label>
                <input
                  type="text"
                  name="address"
                  value={values.address}
                  onChange={handleChange}
                  className="outline-none font-sans w-full p-3 border border-gray-200 rounded-full text-[#000E10] text-base focus:border-emerald-500 transition-colors"
                  
                />
              </div>

              {/* KYC Metadata */}
              <div className="space-y-4">
                <div>
                  <label className="block mb-2 text-sm font-semibold text-gray-700">ID Type</label>
                  <select
                    name="kyc_metadata.id_type"
                    value={values.kyc_metadata.id_type}
                    onChange={handleChange}
                    className="outline-none font-sans w-full p-3 border border-gray-200 rounded-full text-[#000E10] text-sm focus:border-emerald-500 transition-colors bg-white"
                  >
                    {getIdOptions(values.country).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block mb-2 text-sm font-semibold text-gray-700">ID Number</label>
                  <input
                    type="text"
                    name="kyc_metadata.id_number"
                    value={values.kyc_metadata.id_number}
                    onChange={handleChange}
                    className="outline-none font-sans w-full p-3 border border-gray-200 rounded-full text-[#000E10] text-base focus:border-emerald-500 transition-colors"
                    
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block mb-2 text-sm font-semibold text-gray-700">KYC Status</label>
                  <select
                    name="kyc_status"
                    value={values.kyc_status}
                    onChange={handleChange}
                    className="outline-none font-sans w-full p-3 border border-gray-200 rounded-full text-[#000E10] text-sm focus:border-emerald-500 transition-colors bg-white"
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="declined">Declined</option>
                  </select>
                </div>
              </div>
            </div>

           <div className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:grid-cols-3">
              {/* Account Status */}
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={values.isActive}
                  onChange={handleChange}
                  className="w-5 h-5 cursor-pointer accent-emerald-600"
                />
                <label htmlFor="isActive" className="text-sm font-semibold text-gray-700 cursor-pointer">
                  Account Active
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="isHighRisk"
                  name="isHighRisk"
                  checked={values.isHighRisk}
                  onChange={handleChange}
                  className="w-5 h-5 cursor-pointer accent-emerald-600"
                />
                <label htmlFor="isHighRisk" className="text-sm font-semibold text-gray-700 cursor-pointer">
                  High Risk Customer
                </label>
              </div>
              
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="isOtcCustomer"
                  name="isOtcCustomer"
                  checked={values.isOtcCustomer}
                  onChange={handleChange}
                  className="w-5 h-5 cursor-pointer accent-emerald-600"
                />
                <label htmlFor="isOtcCustomer" className="text-sm font-semibold text-gray-700 cursor-pointer">
                  OTC Customer
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="space-y-4">
                <div>
                  <label className="block mb-2 text-sm font-semibold text-gray-700">OTC Percentage (%)</label>
                  <input
                    type="number"
                    name="otcPlatformPercentage"
                    value={values.otcPlatformPercentage}
                    onChange={handleChange}
                    className="outline-none font-sans w-full p-3 border border-gray-200 rounded-full text-[#000E10] text-base focus:border-emerald-500 transition-colors"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block mb-2 text-sm font-semibold text-gray-700">Reserve Percentage (%)</label>
                  <input
                    type="number"
                    name="reservePercentage"
                    value={values.reservePercentage}
                    onChange={handleChange}
                    className="outline-none font-sans w-full p-3 border border-gray-200 rounded-full text-[#000E10] text-base focus:border-emerald-500 transition-colors"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <button
                type="submit"
                disabled={isPending}
                className="px-10 py-3 bg-emerald-800 text-white rounded-full font-bold text-base hover:bg-emerald-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[160px]"
              >
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditUser;
